import React, { useState } from "react";
import { styled } from "@mui/material/styles";

import { Checkbox, IconButton, Paper } from "@mui/material";
import {
  Description,
  GetApp as DownloadIcon,
  Image,
  ZoomOutMap
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import { withTransientProps } from "core/utils/transientProps";
import { IAttachment } from "components/Attachments/IAttachment";
import { AttachmentLightbox } from "components/AttachmentLightbox/AttachmentLightbox";

export interface AttachmentListItemProps {
  attachment: IAttachment;
  isInitiallySelected: boolean;
  onSelected?: (isSelected: boolean) => void;
}

export const AttachmentListItem = ({
  attachment,
  isInitiallySelected = false,
  onSelected
}: AttachmentListItemProps): JSX.Element => {
  const [isSelected, setIsSelected] = useState(isInitiallySelected);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const isImage = attachment.type.startsWith("image");

  async function onClickExpand(): Promise<void> {
    if (attachment.retrieveImagePreview === undefined) return;

    setPreviewUri(await attachment.retrieveImagePreview());
  }

  async function onClickDownloadFile(): Promise<void> {
    if (attachment.downloadFile === undefined) return;

    await attachment.downloadFile();
  }

  function onSelectedChanged(selected: boolean): void {
    setIsSelected(selected);

    if (onSelected !== undefined) onSelected(selected);
  }

  return (
    <>
      <AttachmentWrapper role="listitem">
        <StartSection>
          {onSelected && (
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={evt => onSelectedChanged(evt.target.checked)}
              color="primary"
            />
          )}
          {isImage ? <ImageIcon /> : <FileIcon />}
          <FilenameText>{attachment.filename}</FilenameText>
        </StartSection>
        <Section>
          {attachment.attachedByUser && (
            <Tooltip
              title={`Attached by ${attachment.attachedByUser.first_name} ${attachment.attachedByUser.last_name}`}
            >
              <AttachedByText>{`${attachment.attachedByUser.first_name} ${attachment.attachedByUser.last_name}`}</AttachedByText>
            </Tooltip>
          )}
          <ButtonContainer $deleteAllowed={Boolean(attachment.onDelete)}>
            {attachment.retrieveImagePreview && (
              <IconButton
                aria-label="expand image"
                title="Expand image"
                onClick={onClickExpand}
                size="large"
              >
                <ZoomOutMap />
              </IconButton>
            )}
            {attachment.downloadFile && (
              <IconButton
                aria-label="download attachment"
                title="Download"
                onClick={onClickDownloadFile}
                size="large"
              >
                <DownloadIcon />
              </IconButton>
            )}
            {attachment.onDelete && (
              <IconButton
                aria-label="delete attachment"
                title="Delete"
                onClick={attachment.onDelete}
                size="large"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </ButtonContainer>
        </Section>
      </AttachmentWrapper>
      {previewUri && (
        <AttachmentLightbox
          attachmentUrl={previewUri}
          onClose={() => setPreviewUri(null)}
        />
      )}
    </>
  );
};

const AttachmentWrapper = styled(Paper)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  gap: 8px;
  flex-grow: 1;
  overflow: hidden;
`;

const Section = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StartSection = styled(Section)`
  overflow: hidden;
`;

const FilenameText = styled("p")`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const AttachedByText = styled("p")`
  padding: 1px 8px;
  background-color: #d9d9d9;
  border-radius: 3px;
  font-size: 11px;
  white-space: nowrap;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ButtonContainer = styled("div", withTransientProps)<{
  $deleteAllowed: boolean;
}>`
  display: flex;
  gap: 8px;
  align-items: center;
  // min-width so that attached by appears in the correct position
  min-width: ${props => (props.$deleteAllowed ? "100px" : "64px")};
  justify-content: flex-end;
`;

const ImageIcon = styled(Image)`
  color: gray;
`;

const FileIcon = styled(Description)`
  color: gray;
`;
